import React from 'react';

import { useQuery, useSuspenseQuery, useQueryClient, useMutation, queryOptions } from '@tanstack/react-query';
import {
  getAvailableSuffixes, getDisplayName, getInsurances, getPatientBetterhelpCoverage, getNotificationPreferences, getProviderAvatarAsset, getProviderCPTCodes, getProviderLicenseTypes,
  getUser, getUserContacts, getUserEmergencyContact, getUserMatches, getUserProfile,
  InsuranceCompany, Match, MatchPreference, MatchState, patchUserProfile, PatchUserProfileInput, ProviderOverview, PublicUser, PublicUserProfile,
  patchNotificationPreferences, UpdateNotificationPreferencesInput,
} from '@uplift/api';
import { CamelCaseKeys } from '@uplift/types';

import { BaseQueryOpts, BaseSuspenseQueryOpts } from '../baseTypes';
import { useCountyQuery, useStateQuery } from '../geo.queries';

export const useUserQuery = (userId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', userId, 'basic' ],
    queryFn: async () => {
      if (!userId) return null;
      const userResponse = await getUser(userId);

      return userResponse.data;
    },
    ...opts
  });
};


export const useUserAvatarAssetsQuery = (userId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', userId, 'avatar' ],
    queryFn: async () => {
      if (!userId) return null;
      const response = await getProviderAvatarAsset(userId);

      return response.data;
    },
    ...opts,
  });
};


export const useUserProfileQuery = (userId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', userId, 'profile' ],
    queryFn: async () => {
      if (!userId) return null;
      const userProfileResponse = await getUserProfile(userId);

      return userProfileResponse.data;
    },
    ...opts
  });
};


export const useUserProfileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { userId: string; profile: PatchUserProfileInput }) => {
      const response = await patchUserProfile(params.userId, params.profile);

      return response.data;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: [ 'user', variables.userId, 'profile' ] });
    },
  });
};


export const useUserAddressQuery = (userId: string, opts?: BaseQueryOpts) => {
  const { data: profileData, isLoading: isProfileLoading } = useUserProfileQuery(userId, opts);
  const { data: stateData, isLoading: isStateLoading } = useStateQuery(profileData?.address?.stateId);
  const { data: countyData, isLoading: isCountyLoading } = useCountyQuery(profileData?.address?.zip);

  return {
    isLoading: isProfileLoading || isStateLoading || isCountyLoading,
    data: {
      address: profileData?.address,
      state: stateData,
      county: countyData,
    }
  };
};


export const userContactsQueryKey = (userId: string) => [ 'user', userId, 'contacts' ];
export const userContactsQuery = (userId: string) => queryOptions({
  queryKey: [ 'user', userId, 'contacts' ],
  queryFn: async () => {
    const contactsResponse = await getUserContacts(userId);

    return contactsResponse.data;
  },
  staleTime: Infinity,
});
export const useUserContactsQuery = (userId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    ...userContactsQuery(userId),
    ...opts
  });
};


export type UserMatchOverview = {
  match: Match;
  matchPreferences: MatchPreference;
  patientProfile: PublicUserProfile;
  provider: PublicUser;
  providerProfile: PublicUserProfile;
  providerOverview: ProviderOverview;
};


export const useUserMatchesQuery = (userId: string, states?: MatchState[], opts?: BaseQueryOpts) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [ 'user', userId, 'matches', states ],
    queryFn: async () => {
      const matchesResponse = await getUserMatches({ userId, states });

      const matchPreferencesByMatchId = matchesResponse.data.matchPreferences.reduce<Record<string, MatchPreference>>((prev, curr) => ({ ...prev, [curr.matchId]: curr }), {});
      const matchUsersById = matchesResponse.data.users.reduce<Record<string, PublicUser>>((prev, curr) => ({ ...prev, [curr.id]: curr }), {});
      const matchUserProfilesById = matchesResponse.data.profiles.reduce<Record<string, PublicUserProfile>>((prev, curr) => ({ ...prev, [curr.userId]: curr }), {});
      const matchProviderOverviewById = matchesResponse.data.providerOverviews.reduce<Record<string, ProviderOverview>>((prev, curr) => ({ ...prev, [curr.userId]: curr }), {});
      const matches: UserMatchOverview[] = matchesResponse.data.matches.map(match => {
        const matchProviderId = match.providerId ?? '';

        return {
          match: match,
          matchPreferences: matchPreferencesByMatchId[match.id],
          patientProfile: matchUserProfilesById[match.patientId],
          provider: matchUsersById[matchProviderId],
          providerProfile: matchUserProfilesById[matchProviderId],
          providerOverview: matchProviderOverviewById[matchProviderId],
        };
      });

      return {
        allMatches: matches.filter(m => !!m.match.providerId),
        activeMatches: matches.filter(m => m.match.state === 'active'),
        archivedMatches: matches.filter(m => m.match.state === 'archived'),
      };
    },
    ...opts,
    onSuccess: (data) => {
      data.allMatches.forEach(m => {
        if (m.provider) {
          queryClient.setQueryData([ 'user', m.provider.id, 'basic' ], m.provider);
          queryClient.setQueryData([ 'user', m.provider.id, 'profile' ], m.providerProfile);
        }
      });
    }
  });
};


export const usePatientMatchedProvider = (patientId: string, providerId?: string, states?: MatchState[], opts?: BaseQueryOpts) => {
  const query = useUserMatchesQuery(patientId, states, opts);

  const matchedProvider = React.useMemo(() => {
    if (!providerId) {
      return null;
    }

    const providerMatch = query.data?.allMatches?.find(m => m.provider.id === providerId);

    if (!providerMatch) {
      return null;
    }

    return {
      user: providerMatch.provider,
      profile: providerMatch.providerProfile,
      overview: providerMatch.providerOverview,
      match: providerMatch.match,
    };
  }, [ query.data, providerId ]);

  return {
    ...query,
    data: matchedProvider,
  };
};


export const useIsActiveMatchBetweenPatientAndProvider = (patientId: string, providerId?: string, opts?: BaseQueryOpts) => {
  const query = useUserMatchesQuery(patientId, [ 'active' ], opts);

  const isActiveMatch = React.useMemo(() => {
    if (!providerId) {
      return null;
    }

    const providerMatch = query.data?.activeMatches?.find(m => m.provider.id === providerId);

    return !!providerMatch;
  }, [ query.data, providerId ]);

  return isActiveMatch;
};


export const useUserInsurancesQuery = (userId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', userId, 'insurances' ],
    queryFn: async () => {
      const insurancesResponse = await getInsurances(userId);
      const insuranceCompanyById = insurancesResponse.data.insuranceCompanies.reduce<Record<string, CamelCaseKeys<InsuranceCompany>>>((prev, curr) => ({ ...prev, [curr.id]: curr }), {});

      return insurancesResponse.data.insurances.map(insurance => ({
        insurance: insurance,
        insuranceCompany: insurance?.companyId && insuranceCompanyById[insurance?.companyId],
      }));
    },
    ...opts
  });
};


export const useBetterhelpCoverageQuery = (patientId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', patientId, 'betterhelp-coverage' ],
    queryFn: async () => (await getPatientBetterhelpCoverage(patientId)).data,
    ...opts
  });
};


export const useUserEmergencyContactQuery = (userId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', userId, 'emergencyContact' ],
    queryFn: async () => {
      const emergencyContactResponse = await getUserEmergencyContact(userId);

      return emergencyContactResponse.data;
    },
    ...opts
  });
};


export const useProviderCptCodesQuery = (providerId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', providerId, 'cptCodes' ],
    queryFn: async () => {
      if (!providerId) {
        return {
          cptCodes: [],
          filterOutRules: [],
        };
      }
      const response = await getProviderCPTCodes();

      return {
        cptCodes: response.data.cptCodes,
        filterOutRules: response.data.filterOutRules,
      };
    },
    ...opts
  });
};


export const userDisplayNameQuery = (userId?: string) => queryOptions({
  queryKey: [ 'user', userId, 'displayName' ],
  queryFn: async () => {
    if (!userId) {
      return null;
    }

    const response = await getDisplayName(userId);

    if (!response) {
      return null;
    }

    return response.data;
  },
  staleTime: Infinity,
});

export const useUserDisplayNameQuery = (userId?: string, opts?: BaseQueryOpts) =>
  useQuery({ ...userDisplayNameQuery(userId), ...opts });

export const useProviderLicenseTypesQuery = (userId?: string, opts?: BaseQueryOpts) =>
  useQuery({
    ...opts,
    queryKey: [ 'provider', userId, 'licenseTypes' ],
    queryFn: async () => {
      if (!userId) return [];
      const response = await getProviderLicenseTypes(userId);

      return response.data;
    },
  });

export const useNotificationPreferencesQuery = (userId: string, opts?: BaseQueryOpts) =>
  useQuery({
    queryKey: [ 'user', userId, 'notificationPreferences' ],
    queryFn: async () => {
      const response = await getNotificationPreferences();

      return response.data;
    },
    ...opts,
  });

export const useNotificationPreferencesMutation = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateNotificationPreferencesInput) => {
      const response = await patchNotificationPreferences(input);

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [ 'user', userId, 'notificationPreferences' ] });
    },
  });
};

export const useAvailableSuffixesQuery = (opts?: BaseSuspenseQueryOpts) =>
  useSuspenseQuery({
    queryKey: [ 'availableSuffixes' ],
    queryFn: async () => {
      const response = await getAvailableSuffixes();

      return response.data;
    },
    ...opts,
  });
